import React, { useEffect, useState } from 'react';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { validateEmail, validatePassword } from '../utils/security';
import { auth } from '../firebase';
import './FirebaseAuth.css';

const provider = new GoogleAuthProvider();

// Configure Google provider
provider.setCustomParameters({
  prompt: 'select_account'
});

import logo from '../assets/logo.svg';

const MESSAGES = {
  fr: {
    login: 'Connexion',
    register: 'Créer un compte',
    email: 'Adresse e-mail',
    password: 'Mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    submit: 'Se connecter',
    submitRegister: 'Créer un compte',
    loading: 'Chargement...',
    logout: 'Se déconnecter',
    loggingOut: 'Déconnexion...',
    loggedAs: 'Connecté en tant que',
    or: 'ou',
    google: 'Se connecter avec Google',
    switchToRegister: 'Créer un compte',
    switchToLogin: 'Déjà un compte ? Se connecter',
    invalidEmail: 'Entre un email valide.',
    invalidPassword: 'Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre.',
    passwordMismatch: 'Les mots de passe ne correspondent pas.',
    emptyPassword: 'Entre un mot de passe.',
    userNotFound: 'Aucun compte trouvé avec cet email.',
    wrongPassword: 'Mot de passe incorrect.',
    emailInUse: 'Un compte existe déjà avec cet email.',
  },
  en: {
    login: 'Sign in',
    register: 'Create account',
    email: 'Email address',
    password: 'Password',
    confirmPassword: 'Confirm password',
    submit: 'Sign in',
    submitRegister: 'Create account',
    loading: 'Loading...',
    logout: 'Sign out',
    loggingOut: 'Signing out...',
    loggedAs: 'Signed in as',
    or: 'or',
    google: 'Sign in with Google',
    switchToRegister: 'Create account',
    switchToLogin: 'Already have an account? Sign in',
    invalidEmail: 'Enter a valid email.',
    invalidPassword: 'Password must contain at least 8 characters, one uppercase, one lowercase and one number.',
    passwordMismatch: 'Passwords don\'t match.',
    emptyPassword: 'Enter a password.',
    userNotFound: 'No account found with this email.',
    wrongPassword: 'Incorrect password.',
    emailInUse: 'An account already exists with this email.',
  }
};

const FirebaseAuth = ({ onAuth }) => {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState('');
  const [errorField, setErrorField] = useState(''); // 'email' | 'password' | 'confirm' | 'general'
  const [loading, setLoading] = useState(false);
  const lang = localStorage.getItem('voyx-lang') || 'fr';
  const t = MESSAGES[lang] || MESSAGES['fr'];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (user && onAuth) onAuth(user);
    });
    return () => unsubscribe();
  }, [onAuth]);

  const handleGoogle = async () => {
    try {
      setLoading(true);
      setError('');
      await signInWithPopup(auth, provider);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    setError('');
    setErrorField('');
    setLoading(true);

    try {
      if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        throw { custom: t.invalidEmail, field: 'email' };
      }
      if (!password) {
        throw { custom: t.emptyPassword, field: 'password' };
      }

      if (!isLogin) {
        if (!validatePassword(password)) {
          throw { custom: t.invalidPassword, field: 'password' };
        }
        if (password !== confirmPassword) {
          throw { custom: t.passwordMismatch, field: 'confirm' };
        }
      }

      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
    } catch (e) {
      let errorMessage = e.custom || e.message;
      let field = e.field || 'email';
      if (e.code === 'auth/user-not-found') {
        errorMessage = t.userNotFound;
        field = 'email';
      } else if (e.code === 'auth/wrong-password') {
        errorMessage = t.wrongPassword;
        field = 'password';
      } else if (e.code === 'auth/email-already-in-use') {
        errorMessage = t.emailInUse;
        field = 'email';
      }
      setError(errorMessage);
      setErrorField(field);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      await signOut(auth);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (user) {
    return (
      <div className="auth-container">
        <div className="auth-nav">
          <img
            src={logo}
            alt="VOYX Logo"
            className="auth-logo"
            onClick={() => window.location.href = '/'}
          />
        </div>
        <div className="auth-form">
          <p>{t.loggedAs} <b>{user.email}</b></p>
          <button
            onClick={handleLogout}
            disabled={loading}
            className="auth-form button"
          >
            {loading ? t.loggingOut : t.logout}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-nav">
        <img
          src={logo}
          alt="VOYX Logo"
          className="auth-logo"
          onClick={() => window.location.href = '/'}
        />
      </div>
      <div className="auth-form">
        <h2>{isLogin ? t.login : t.register}</h2>
        <form onSubmit={handleEmail}>
          <input
            type="email"
            placeholder={errorField === 'email' ? error : t.email}
            className={errorField === 'email' ? 'input-error' : ''}
            value={email}
            onChange={e => { setEmail(e.target.value); if (errorField === 'email') { setError(''); setErrorField(''); } }}
          />
          <input
            type="password"
            placeholder={errorField === 'password' ? error : t.password}
            className={errorField === 'password' ? 'input-error' : ''}
            value={password}
            onChange={e => { setPassword(e.target.value); if (errorField === 'password') { setError(''); setErrorField(''); } }}
          />
          {!isLogin && (
            <input
              type="password"
              placeholder={errorField === 'confirm' ? error : t.confirmPassword}
              className={errorField === 'confirm' ? 'input-error' : ''}
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); if (errorField === 'confirm') { setError(''); setErrorField(''); } }}
            />
          )}
          <button
            type="submit"
            disabled={loading}
            className="auth-form button"
          >
            {loading ? t.loading : (isLogin ? t.submit : t.submitRegister)}
          </button>
        </form>
        <div className="divider">
          <span>{t.or}</span>
        </div>
        <button
          onClick={handleGoogle}
          disabled={loading}
          className="google-button"
        >
          <img src="https://www.google.com/favicon.ico" alt="Google" />
          {t.google}
        </button>
        <button
          onClick={() => {
            setIsLogin(!isLogin);
            setError('');
            setErrorField('');
            setPassword('');
            setConfirmPassword('');
          }}
          disabled={loading}
          className="switch-mode"
        >
          {isLogin ? t.switchToRegister : t.switchToLogin}
        </button>
      </div>
    </div>
  );
};

export default FirebaseAuth; 